import { ComponentCustomProperties, createApp, reactive, readonly } from 'vue';
import App from './App.vue';
import router from './router';

import { IonicVue } from '@ionic/vue';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/* Theme variables */
import './theme/variables.css';
import '@ionic/vue/css/palettes/dark.system.css';

import 'tailwindcss/tailwind.css';

import './theme/global.css'

// Locales
import en from './assets/locales/en.json'
// import es from './assets/locales/es.json'

// Translations
import {createI18n} from "petite-vue-i18n";

// Pinia
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import {useFeatureFlagStore} from '@/stores/featureFlags'

const i18n = createI18n({
  legacy: false,
  fallbackLocale: 'en',
  locale: 'en',
  globalInjection: true,
  messages: {
    en
  }
})

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
const app = createApp(App).use(pinia).use(IonicVue).use(router).use(i18n);
useFeatureFlagStore().loadFeatureFlags();

// Replace require.context with Vite's dynamic import
const modules = import.meta.glob<{ default: any }>('../src/views/Shared/**/[A-Z]*.vue', { eager: true });

Object.entries(modules).forEach(([path, module]) => {
  // Gets the file name regardless of folder depth
  const componentName = path.split('/').pop()?.replace(/\.\w+$/, '') || '';
  
  // Get component config
  const componentConfig = module as { default: any };

  app.component(
    componentName,
    // Look for the component options on `.default`, which will
    // exist if the component was exported with `export default`,
    // otherwise fall back to module's root.
    componentConfig.default || componentConfig,
  );
});

interface ToastState {
  show: boolean
  title: string
  message: string
  successful: boolean
  id: symbol | null
}

const toastState = reactive<ToastState>({
  show: false,
  title: '',
  message: '',
  successful: true,
  id: null,
});

const loadLanguage = async (locale: string) => {
  if (!i18n.global.availableLocales.includes(locale as any)) {
    const messages = await import(`./assets/locales/${locale}.json`);
    i18n.global.setLocaleMessage(locale, messages.default);
  }
}

app.config.globalProperties.$switchLanguage = async (newLocale: string) => {
  await loadLanguage(newLocale);
  i18n.global.locale.value = newLocale as any
}

app.config.globalProperties.$toastState = readonly(toastState);

app.config.globalProperties.$toast = (success: boolean, title: string, message = '') => {
  const id = Symbol();

  toastState.id = id;
  toastState.successful = success;
  toastState.title = title;
  toastState.message = message;
  toastState.show = true;

  return new Promise<void>((resolve) => {
    setTimeout(() => {
      if (toastState.id === id && toastState.show) toastState.show = false;
      resolve();
    }, success ? 3000 : 12000);
  });
}

app.config.globalProperties.$closeToast = () => {
  toastState.show = false;
}

export const loadedGoogleMapsAPI = new Promise( (resolve,reject) => {
  window['mapApiInitialized'] = resolve;

  const map = document.createElement('script');

  map.setAttribute('src',
      `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAP_KEY}&libraries=places,geometry,marker&callback=mapApiInitialized&loading=async`);
  map.setAttribute('id', 'map-api-script');

  document.body.appendChild(map);
});

router.isReady().then(() => {
  app.mount('#app');
});
